import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ControlBridgeIssuesQueryError,
  fetchWorkspaceIssues,
  type WorkspaceIssueRow,
} from '../../../services/controlBridgeIssuesQueryService';
import {
  ControlBridgeIssueTransitionError,
  transitionControlBridgeIssue,
  type IssueTransitionAction,
} from '../../../services/controlBridgeIssueTransitionService';
import { EmptyState, SectionLabel } from '../shared/SurfaceParts';
import styles from '../ControlBridge.module.css';

export interface IssuesInboxProps {
  workspaceSlug: string;
  focusItemId?: string | null;
  onOpenItem: (itemId: string, label: string) => void;
  /** Bump to force reload after Report issue. */
  refreshToken?: number;
  /** Hide item ids and state-machine copy when embedded on Diagnostics. */
  hideItemIds?: boolean;
}

function displaySummary(row: WorkspaceIssueRow): string {
  return row.issueSummary || row.name;
}

function clientIssueState(state?: string, hideMachinery?: boolean): string {
  const raw = (state || '').trim();
  if (!hideMachinery) return raw || '—';
  const s = raw.toUpperCase();
  if (s === 'CLOSED' || s === 'RESOLVED' || s === 'DONE' || s === 'CANCELLED') return 'Closed';
  return 'Open';
}

function formatDiscoveredAt(raw?: string): string | null {
  if (!raw) return null;
  const ms = Date.parse(raw);
  if (!Number.isFinite(ms)) return raw;
  try {
    return new Date(ms).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return raw;
  }
}

function canAdvance(state?: string): boolean {
  const s = (state || '').trim().toUpperCase();
  return s === 'INITIALIZED' || s === 'CREATED' || s === 'DRAFTED' || s === 'ACTIVE';
}

function canClose(state?: string): boolean {
  const s = (state || '').trim().toUpperCase();
  return s === 'INITIALIZED' || s === 'CREATED' || s === 'DRAFTED' || s === 'ACTIVE';
}

export const IssuesInbox: React.FC<IssuesInboxProps> = ({
  workspaceSlug,
  focusItemId,
  onOpenItem,
  refreshToken = 0,
  hideItemIds = false,
}) => {
  const [rows, setRows] = useState<WorkspaceIssueRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pendingItemId, setPendingItemId] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [total, setTotal] = useState(0);
  const openedFocusRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    if (!workspaceSlug.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const next = await fetchWorkspaceIssues(workspaceSlug);
      setRows(next.rows);
      setTruncated(next.truncated);
      setTotal(next.total);
    } catch (err) {
      const message =
        err instanceof ControlBridgeIssuesQueryError
          ? err.message
          : 'Failed to load issues';
      setError(message);
      setRows([]);
      setTruncated(false);
      setTotal(0);
    } finally {
      setBusy(false);
    }
  }, [workspaceSlug]);

  useEffect(() => {
    void load();
  }, [load, refreshToken]);

  useEffect(() => {
    if (!focusItemId) {
      openedFocusRef.current = null;
      return;
    }
    if (openedFocusRef.current === focusItemId) return;
    const hit = rows.find((r) => r.itemId === focusItemId);
    if (!hit) return;
    openedFocusRef.current = focusItemId;
    onOpenItem(hit.itemId, displaySummary(hit));
  }, [focusItemId, rows, onOpenItem]);

  const runTransition = useCallback(
    async (row: WorkspaceIssueRow, action: IssueTransitionAction) => {
      setActionError(null);
      setPendingItemId(row.itemId);
      try {
        await transitionControlBridgeIssue(workspaceSlug, {
          itemId: row.itemId,
          action,
        });
        await load();
      } catch (err) {
        const message =
          err instanceof ControlBridgeIssueTransitionError
            ? err.message
            : 'Failed to update issue';
        setActionError(message);
      } finally {
        setPendingItemId(null);
      }
    },
    [workspaceSlug, load]
  );

  return (
    <div className={styles.surfaceRoot} data-testid="issues-inbox">
      <section aria-label="Workspace issues">
        {hideItemIds ? null : <SectionLabel>Issues</SectionLabel>}
        <p className={styles.pageSubtitle}>
          {hideItemIds
            ? 'Open issues for this workspace. Advance moves the issue forward; Resolve closes it.'
            : 'All non-terminal issues for this workspace. Advance moves one step; Resolve closes the issue.'}
        </p>
        <div className={styles.issuesInboxToolbar}>
          <button
            type="button"
            className={styles.reportIssueSecondary}
            onClick={() => void load()}
            disabled={busy || pendingItemId !== null}
            data-testid="issues-inbox-refresh"
          >
            {busy ? 'Loading…' : 'Refresh'}
          </button>
        </div>
        {error ? (
          <p className={styles.reportIssueError} role="alert">
            {error}
          </p>
        ) : null}
        {actionError ? (
          <p className={styles.reportIssueError} role="alert" data-testid="issues-inbox-action-error">
            {actionError}
          </p>
        ) : null}
        {truncated ? (
          <p className={styles.issuesInboxTruncation} role="status">
            Showing {rows.length} of {total} matching rows (query cap). Narrow by closing
            resolved issues or raise the cap in a follow-on.
          </p>
        ) : null}
        {!busy && !error && rows.length === 0 ? <EmptyState label="open issues" /> : null}
        {rows.length > 0 ? (
          <ul className={styles.issuesInboxList} data-testid="issues-inbox-list">
            {rows.map((row) => {
              const focused = focusItemId === row.itemId;
              const summary = displaySummary(row);
              const discovered = formatDiscoveredAt(row.discoveredAt);
              const pending = pendingItemId === row.itemId;
              const showAdvance = canAdvance(row.currentState);
              const showClose = canClose(row.currentState);
              return (
                <li
                  key={row.itemId}
                  className={`${styles.issuesInboxCard}${focused ? ` ${styles.issuesInboxRowFocused}` : ''}`}
                  data-testid={`issues-inbox-row-${row.itemId}`}
                >
                  <button
                    type="button"
                    className={styles.issuesInboxOpen}
                    onClick={() => onOpenItem(row.itemId, summary)}
                    data-testid={`issues-inbox-open-${row.itemId}`}
                  >
                    <span className={styles.issuesInboxTitle}>{summary}</span>
                    {row.issueDescription ? (
                      <span className={styles.issuesInboxDescription}>{row.issueDescription}</span>
                    ) : null}
                    <span className={styles.issuesInboxMeta}>
                      <span className={styles.mcStatusPill}>
                        {clientIssueState(row.currentState, hideItemIds)}
                      </span>
                      {row.issueSeverity ? (
                        <span className={styles.issuesInboxSeverity}>
                          {row.issueSeverity.toUpperCase()}
                        </span>
                      ) : null}
                      {row.issueType ? (
                        <span className={styles.issuesInboxType}>{row.issueType}</span>
                      ) : null}
                      {discovered ? (
                        <span className={styles.issuesInboxDiscovered}>{discovered}</span>
                      ) : null}
                      {hideItemIds ? null : (
                        <code className={styles.issuesInboxId}>{row.itemId}</code>
                      )}
                    </span>
                  </button>
                  {(showAdvance || showClose) && (
                    <div className={styles.issuesInboxActions}>
                      {showAdvance ? (
                        <button
                          type="button"
                          className={styles.reportIssueSecondary}
                          disabled={pending || busy}
                          onClick={() => void runTransition(row, 'advance')}
                          data-testid={`issues-inbox-advance-${row.itemId}`}
                        >
                          {pending ? 'Working…' : 'Advance'}
                        </button>
                      ) : null}
                      {showClose ? (
                        <button
                          type="button"
                          className={styles.reportIssuePrimary}
                          disabled={pending || busy}
                          onClick={() => void runTransition(row, 'close')}
                          data-testid={`issues-inbox-resolve-${row.itemId}`}
                        >
                          {pending ? 'Working…' : 'Resolve'}
                        </button>
                      ) : null}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        ) : null}
      </section>
    </div>
  );
};
